import { useEffect } from 'react';
import { useChatStore } from '@/lib/chat-store';

type MessageLoaderHookParams = {
  siteId: string;
  themeId: string;
  themeBuildId: string;
  enabled?: boolean;
};

export function useLoadMessages({ siteId, themeId, themeBuildId, enabled = true }: MessageLoaderHookParams) {
  const { addMessage } = useChatStore();

  useEffect(() => {
    if (!enabled || !siteId || !themeId || !themeBuildId) return;

    const loadMessages = async () => {
      try {
        useChatStore.getState().clearMessages();
        const response = await fetch(
          `/sites/${siteId}/themes/${themeId}/theme_builds/${themeBuildId}/ai_code_assistants/messages`,
          {
            headers: {
              'Content-Type': 'application/json',
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          
          // Load messages into the chat store
          if (data.messages && Array.isArray(data.messages)) {
            // Clear existing messages first
            useChatStore.getState().clearMessages();
            
            // Add each message
            data.messages.forEach((message: any) => {
              if (message.role) {
                // Parse tool_calls if present and convert to agentActions format
                let agentActions = undefined;
                if (message.tool_calls) {
                  try {
                    const toolCalls = typeof message.tool_calls === 'string' 
                      ? JSON.parse(message.tool_calls) 
                      : message.tool_calls;
                    
                    // Convert tool_calls array to agentActions format
                    const toolCallsArray = Array.isArray(toolCalls) ? toolCalls : [toolCalls];
                    agentActions = toolCallsArray.map((tc: any) => ({
                      tool_call_id: tc.id || tc.tool_call_id,
                      function_name: tc.function?.name || tc.function_name,
                      arguments: tc.function?.arguments || tc.arguments,
                      result: tc.result
                    }));
                  } catch (e) {
                    console.error('Failed to parse tool_calls:', e);
                  }
                }
                
                addMessage({
                  role: message.role,
                  content: message.content || '',
                  agentActions: agentActions,
                  imageUrl: message.image_url || undefined
                });
              }
            });
            console.log(`Loaded ${data.messages.length} messages from backend`);
          }
        }
      } catch (error) {
        console.error('Failed to load messages:', error);
      }
    };

    loadMessages();
  }, [siteId, themeId, themeBuildId, enabled, addMessage]);
}
